<template>
  <div class="dashboard-grid" :style="gridStyle" @click="clearSelection">
    <!-- Grid Spots (shown when widget is being resized or dragged) -->
    <template v-if="resizingWidget || draggingWidget">
      <div
        v-for="spot in gridSpots"
        :key="`spot-${spot.x}-${spot.y}`"
        class="grid-spot"
        :class="{ 
          'grid-spot-occupied': isSpotOccupied(spot.x, spot.y),
          'grid-spot-valid': isValidDropSpot(spot.x, spot.y),
          'grid-spot-invalid': !isValidDropSpot(spot.x, spot.y),
          'grid-spot-resize-area': isInResizeArea(spot.x, spot.y),
          'grid-spot-resize-invalid': isInvalidResizeArea(spot.x, spot.y),
          'grid-spot-drag-area': isInDragArea(spot.x, spot.y)
        }"
        :style="getGridSpotStyle(spot.x, spot.y)"
        @click.stop="handleGridSpotClick(spot.x, spot.y)"
      >
        <div class="grid-spot-indicator">
          {{ spot.x }},{{ spot.y }}
        </div>
      </div>
    </template>

    <!-- Widgets -->
    <WidgetContainer
      v-for="widget in widgets"
      :key="widget.id"
      :widget="widget"
      :grid-columns="layout.columns"
      :is-selected="selectedWidget && selectedWidget.id === widget.id"
      @select="selectWidget"
      @edit="handleWidgetEdit"
      @update="handleWidgetUpdate"
      @remove="handleWidgetRemove"
      @start-resize="handleStartResize"
      @resize-preview="handleResizePreview"
      @resize-complete="handleResizeComplete"
      @end-resize="handleEndResize"
      @start-drag="handleStartDrag"
      @drag-preview="handleDragPreview"
      @drag-complete="handleDragComplete"
      @end-drag="handleEndDrag"
    />
    
    <!-- Empty state -->
    <div v-if="!widgets || widgets.length === 0" class="empty-state">
      <v-icon size="64" color="grey lighten-2">mdi-view-dashboard-outline</v-icon>
      <h3 class="text-h5 mt-4 grey--text">No widgets yet</h3>
      <p class="text-body-1 grey--text">Add your first widget to get started</p>
    </div>
  </div>
</template>

<script>
import WidgetContainer from './WidgetContainer.vue'

export default {
  name: 'DashboardGrid',
  components: {
    WidgetContainer
  },
  props: {
    widgets: {
      type: Array,
      default: () => []
    },
    layout: {
      type: Object,
      default: () => ({
        columns: 12,
        gap: 16
      })
    }
  },
  data() {
    return {
      selectedWidget: null,
      resizingWidget: null,
      resizePreview: null,
      draggingWidget: null
    }
  },
  computed: {
    gridStyle() {
      const maxRows = Math.max(8, ...this.widgets.map(w => w.position.y + w.position.height))
      
      return {
        display: 'grid',
        gridTemplateColumns: `repeat(${this.layout.columns}, 1fr)`,
        gridTemplateRows: `repeat(${maxRows}, 100px)`,
        gap: `${this.layout.gap}px`,
        position: 'relative',
        minHeight: `${maxRows * 100 + (maxRows - 1) * this.layout.gap}px`,
        width: '100%'
      }
    },
    gridSpots() {
      const spots = []
      const maxRows = Math.max(8, ...this.widgets.map(w => w.position.y + w.position.height))
      
      for (let y = 0; y < maxRows; y++) {
        for (let x = 0; x < this.layout.columns; x++) {
          spots.push({ x, y })
        }
      }
      return spots
    }
  },
  methods: {
    handleWidgetUpdate(widget) {
      this.$emit('widget-updated', {
        widgetId: widget.id,
        updates: widget
      })
    },
    handleWidgetRemove(widgetId) {
      this.$emit('widget-removed', widgetId)
      if (this.selectedWidget && this.selectedWidget.id === widgetId) {
        this.selectedWidget = null
      }
    },
    handleWidgetResize({ widgetId, position }) {
      this.$emit('widget-updated', widgetId, { position })
    },
    handleWidgetMove({ widgetId, position }) {
      this.$emit('widget-updated', widgetId, { position })
    },
    clearSelection() {
      this.selectedWidget = null
    },
    selectWidget(widget) {
      this.selectedWidget = widget
    },
    dropWidget(x, y) {
      if (!this.selectedWidget) return
      
      const newPosition = {
        x,
        y,
        width: this.selectedWidget.position.width,
        height: this.selectedWidget.position.height
      }
      
      this.moveWidget(this.selectedWidget, x, y)
      this.clearSelection()
    },
    isSpotOccupied(x, y) {
      return this.widgets.some(widget => {
        const pos = widget.position
        return x >= pos.x && x < pos.x + pos.width &&
               y >= pos.y && y < pos.y + pos.height
      })
    },
    isValidDropSpot(x, y) {
      if (this.resizingWidget) {
        // For resize operations, check if the spot is within the resize area
        return this.isInResizeArea(x, y)
      }
      
      if (this.draggingWidget) {
        // For drag operations, allow dropping anywhere - we'll handle collisions
        const widget = this.draggingWidget
        const newX = x
        const newY = y
        
        // Check if widget fits within grid bounds
        if (newX + widget.position.width > this.layout.columns) return false
        if (newX < 0 || newY < 0) return false
        
        return true
      }
      
      return false
    },
    getGridSpotStyle(x, y) {
      return {
        gridColumn: `${x + 1} / span 1`,
        gridRow: `${y + 1} / span 1`,
        position: 'relative',
        zIndex: 1000
      }
    },
    getWidgetAt(x, y) {
      return this.widgets.find(widget => {
        const pos = widget.position
        return x >= pos.x && x < pos.x + pos.width &&
               y >= pos.y && y < pos.y + pos.height
      })
    },
    moveWidget(widget, newX, newY) {
      this.$emit('widget-updated', widget.id, {
        position: {
          ...widget.position,
          x: newX,
          y: newY
        }
      })
    },
    swapWidgets(widget1, widget2) {
      const pos1 = { ...widget1.position }
      const pos2 = { ...widget2.position }
      
      this.$emit('widget-updated', widget1.id, { position: pos2 })
      this.$emit('widget-updated', widget2.id, { position: pos1 })
    },
    isInResizeArea(x, y) {
      if (!this.resizingWidget || !this.resizePreview) return false
      
      const widget = this.resizingWidget
      const preview = this.resizePreview
      
      return x >= widget.position.x && 
             x < widget.position.x + preview.width &&
             y >= widget.position.y && 
             y < widget.position.y + preview.height
    },
    isInvalidResizeArea(x, y) {
      if (!this.resizingWidget || !this.resizePreview) return false
      
      // For now, allow all resize areas since we handle collisions by pushing
      return false
    },
    handleStartResize(widget) {
      this.resizingWidget = widget
      this.selectedWidget = null
    },
    handleResizePreview({ widgetId, newSize }) {
      this.resizePreview = newSize
    },
    handleResizeComplete({ widgetId, newSize }) {
      if (!this.resizingWidget) return
      
      this.$emit('widget-updated', widgetId, {
        position: {
          ...this.resizingWidget.position,
          width: newSize.width,
          height: newSize.height
        }
      })
      
      this.handleEndResize()
    },
    handleEndResize() {
      this.resizingWidget = null
      this.resizePreview = null
    },
    handleWidgetEdit(widget) {
      this.$emit('widget-edited', widget)
    },
    getOverlappingWidgets(newPosition, excludeId) {
      return this.widgets.filter(widget => {
        if (widget.id === excludeId) return false
        
        const pos = widget.position
        return !(pos.x >= newPosition.x + newPosition.width || 
                pos.x + pos.width <= newPosition.x ||
                pos.y >= newPosition.y + newPosition.height ||
                pos.y + pos.height <= newPosition.y)
      })
    },
    pushOverlappingWidgets(overlappingWidgets, newPosition) {
      overlappingWidgets.forEach(widget => {
        const availablePosition = this.findClosestAvailablePosition(widget, newPosition)
        if (availablePosition) {
          this.$emit('widget-updated', {
            widgetId: widget.id,
            updates: {
              position: {
                ...widget.position,
                x: availablePosition.x,
                y: availablePosition.y
              }
            }
          })
        }
      })
    },
    findClosestAvailablePosition(widget, avoidPosition) {
      const widgetSize = {
        width: widget.position.width,
        height: widget.position.height
      }
      
      // Try positions in expanding circles around the original position
      const originalX = widget.position.x
      const originalY = widget.position.y
      const maxDistance = Math.max(this.layout.columns, 20)
      
      for (let distance = 1; distance <= maxDistance; distance++) {
        // Try positions at this distance
        for (let dx = -distance; dx <= distance; dx++) {
          for (let dy = -distance; dy <= distance; dy++) {
            // Only check positions at the edge of the current distance
            if (Math.abs(dx) !== distance && Math.abs(dy) !== distance) continue
            
            const testX = originalX + dx
            const testY = originalY + dy
            
            // Check if position is valid
            if (testX < 0 || testY < 0 || testX + widgetSize.width > this.layout.columns) continue
            
            const testPosition = {
              x: testX,
              y: testY,
              width: widgetSize.width,
              height: widgetSize.height
            }
            
            // Check if this position is free
            if (this.isPositionFree(testPosition, widget.id, avoidPosition)) {
              return { x: testX, y: testY }
            }
          }
        }
      }
      
      // If no position found, place below the grid
      const maxY = Math.max(...this.widgets.map(w => w.position.y + w.position.height))
      return { x: 0, y: maxY }
    },
    isPositionFree(position, excludeId, avoidPosition) {
      // Check if position overlaps with the avoid position (where new widget is going)
      if (avoidPosition && !(position.x >= avoidPosition.x + avoidPosition.width || 
                            position.x + position.width <= avoidPosition.x ||
                            position.y >= avoidPosition.y + avoidPosition.height ||
                            position.y + position.height <= avoidPosition.y)) {
        return false
      }
      
      // Check if position overlaps with any existing widget (except the one being moved)
      return !this.widgets.some(widget => {
        if (widget.id === excludeId) return false
        
        const pos = widget.position
        return !(position.x >= pos.x + pos.width || 
                position.x + position.width <= pos.x ||
                position.y >= pos.y + pos.height ||
                position.y + position.height <= pos.y)
      })
    },
    handleGridSpotClick(x, y) {
      // Implement the logic to handle spot click
    },
    isInDragArea(x, y) {
      if (!this.draggingWidget) return false
      
      const widget = this.draggingWidget
      const newX = x
      const newY = y
      
      // Check if position is within the widget's area
      return newX >= widget.position.x && newX < widget.position.x + widget.position.width &&
             newY >= widget.position.y && newY < widget.position.y + widget.position.height
    },
    handleStartDrag(widget) {
      this.draggingWidget = widget
      this.selectedWidget = null // Clear selection during drag
    },
    handleDragPreview({ widgetId, newPosition }) {
      this.$emit('widget-updated', widgetId, { position: newPosition })
    },
    handleDragComplete({ widgetId, newPosition }) {
      this.$emit('widget-updated', widgetId, { position: newPosition })
      this.handleEndDrag()
    },
    handleEndDrag() {
      this.draggingWidget = null
    }
  }
}
</script>

<style scoped>
.dashboard-grid {
  min-height: 400px;
  width: 100%;
}

.empty-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
}

/* Grid Spots */
.grid-spot {
  border: 2px dashed transparent;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 80px;
}

.grid-spot-indicator {
  font-size: 10px;
  color: rgba(0, 0, 0, 0.3);
  background: rgba(255, 255, 255, 0.8);
  padding: 2px 4px;
  border-radius: 2px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.grid-spot:hover .grid-spot-indicator {
  opacity: 1;
}

.grid-spot-valid {
  border-color: #4CAF50;
  background: rgba(76, 175, 80, 0.1);
}

.grid-spot-valid:hover {
  background: rgba(76, 175, 80, 0.2);
  border-color: #4CAF50;
}

.grid-spot-invalid {
  border-color: #f44336;
  background: rgba(244, 67, 54, 0.1);
  cursor: not-allowed;
}

.grid-spot-occupied {
  border-color: #FF9800;
  background: rgba(255, 152, 0, 0.1);
}

.grid-spot-occupied:hover {
  background: rgba(255, 152, 0, 0.2);
  border-color: #FF9800;
}

.grid-spot-resize-area {
  border-color: #2196F3;
  background: rgba(33, 150, 243, 0.2);
}

.grid-spot-resize-area:hover {
  background: rgba(33, 150, 243, 0.3);
  border-color: #1976D2;
}

.grid-spot-resize-invalid {
  border-color: #f44336;
  background: rgba(244, 67, 54, 0.2);
  cursor: not-allowed;
}

.grid-spot-resize-invalid:hover {
  background: rgba(244, 67, 54, 0.3);
  border-color: #d32f2f;
}

.grid-spot-drag-area {
  border-color: #2196F3;
  background: rgba(33, 150, 243, 0.2);
}

.grid-spot-drag-area:hover {
  background: rgba(33, 150, 243, 0.3);
  border-color: #1976D2;
}

@media (max-width: 960px) {
  .dashboard-grid {
    grid-template-columns: 1fr !important;
    gap: 12px;
  }
  
  .grid-spot {
    display: none;
  }
}
</style> 